import { For, Show, createEffect, createSignal } from "solid-js";
import { bodyTypes, sizeChartsFor, type BodyType, type ChartStature } from "@tailor-frame/patterns";

/** Where the open body came from: a chart size, an example, a saved profile, or edited by hand. */
export type BodySource =
  | { kind: "chart"; chartId: string; sizeId: string; stature: ChartStature["id"] }
  | { kind: "example"; id: string }
  | { kind: "saved"; id: string }
  | { kind: "custom" };

export interface SavedProfileSummary { id: string; name: string; bodyType: BodyType; from?: string }

interface Props {
  bodyType: BodyType;
  source: BodySource;
  madeFor: string;
  examples: Array<{ id: string; label: string; description: string; bodyType: BodyType }>;
  saved: SavedProfileSummary[];
  onBodyType: (type: BodyType) => void;
  onChartSize: (chartId: string, sizeId: string, stature: ChartStature["id"]) => void;
  onExample: (id: string) => void;
  onSaved: (id: string) => void;
  onDeleteSaved: (id: string) => void;
}

/**
 * Who the clothes are for, in order: the body type, then a size from the
 * book's charts (or an example or saved profile), then the measurements
 * below to customise it, then saved as a profile. Every route fills the same
 * body measurements; each pattern adds its own ease on top.
 */
export function BodyPicker(props: Props) {
  const charts = () => sizeChartsFor(props.bodyType);
  // The chart shown: the one the body came from, else the type's first.
  const [chartId, setChartId] = createSignal(charts()[0].id);
  createEffect(() => {
    const source = props.source;
    if (source.kind === "chart" && charts().some((chart) => chart.id === source.chartId)) setChartId(source.chartId);
    else if (!charts().some((chart) => chart.id === chartId())) setChartId(charts()[0].id);
  });
  const chart = () => charts().find((item) => item.id === chartId()) ?? charts()[0];
  const chosenSize = () => props.source.kind === "chart" && props.source.chartId === chart().id ? props.source.sizeId : undefined;
  const stature = (): ChartStature["id"] => props.source.kind === "chart" ? props.source.stature : "regular";
  const examples = () => props.examples.filter((example) => example.bodyType === props.bodyType);
  const saved = () => props.saved.filter((profile) => profile.bodyType === props.bodyType);

  return (
    <div class="body-picker">
      <p class="picker-step"><small>1</small>Body type</p>
      <div class="body-types" role="radiogroup" aria-label="Body type">
        <For each={bodyTypes}>{(type) => (
          <button type="button" role="radio" aria-checked={props.bodyType === type.id} classList={{ selected: props.bodyType === type.id }} onClick={() => props.onBodyType(type.id)}>
            <strong>{type.label}</strong><small>{type.description}</small>
          </button>
        )}</For>
      </div>

      <p class="picker-step"><small>2</small>Start from a size</p>
      <Show when={charts().length > 1}>
        <div class="chart-tabs" role="tablist" aria-label="Size chart">
          <For each={charts()}>{(item) => <button type="button" role="tab" aria-selected={chart().id === item.id} classList={{ selected: chart().id === item.id }} onClick={() => setChartId(item.id)}>{item.label}</button>}</For>
        </div>
      </Show>
      <div class="size-grid" role="radiogroup" aria-label={`${chart().label} sizes`}>
        <For each={chart().sizes}>{(size) => (
          <button type="button" role="radio" aria-checked={chosenSize() === size.id} classList={{ selected: chosenSize() === size.id }} title={size.hint} onClick={() => props.onChartSize(chart().id, size.id, stature())}>
            <strong>{size.label}</strong><Show when={size.hint}><small>{size.hint}</small></Show>
          </button>
        )}</For>
      </div>
      <Show when={chart().statures}>{(statures) => (
        <div class="segmented-control three-up stature-control" role="group" aria-label="Height">
          <For each={statures()}>{(item) => (
            <button type="button" classList={{ selected: chosenSize() !== undefined && stature() === item.id }} aria-pressed={chosenSize() !== undefined && stature() === item.id} disabled={chosenSize() === undefined} title={`About ${item.heightCm} cm`} onClick={() => props.onChartSize(chart().id, chosenSize()!, item.id)}>
              {item.label} <small>{item.heightCm}</small>
            </button>
          )}</For>
        </div>
      )}</Show>
      <p class="fit-hint chart-source">{chart().source}. Healthy, athletic figures, as in the book.</p>

      <Show when={examples().length > 0 || saved().length > 0}>
        <p class="picker-subhead">or a profile</p>
        <div class="profile-picker">
          <For each={examples()}>{(example) => <button type="button" classList={{ selected: props.source.kind === "example" && props.source.id === example.id }} title={example.description} onClick={() => props.onExample(example.id)}>{example.label}</button>}</For>
          <For each={saved()}>{(profile) => (
            <span class="saved-profile" classList={{ selected: props.source.kind === "saved" && props.source.id === profile.id }}>
              <button type="button" title={profile.from ? `Saved from ${profile.from}` : "Saved profile"} onClick={() => props.onSaved(profile.id)}>{profile.name}</button>
              <button type="button" class="delete-profile" aria-label={`Delete ${profile.name}`} title="Delete this profile" onClick={() => { if (confirm(`Delete the profile “${profile.name}”?`)) props.onDeleteSaved(profile.id); }}>×</button>
            </span>
          )}</For>
        </div>
      </Show>

      <p class="picker-step"><small>3</small>Customise <Show when={props.source.kind === "custom"}><span class="custom-badge">Edited</span></Show></p>
      <p class="fit-hint">Change any measurement below to match the body. Hover, tap or edit one to see where it is taken on the figure.</p>

    </div>
  );
}

/** Step 4, after the measurements: keep this body as a named profile. */
export function SaveProfileForm(props: { source: BodySource; madeFor: string; onSave: (name: string) => void }) {
  const [name, setName] = createSignal("");
  // A saved profile's name is filled in, and kept while it is edited, so saving again updates it.
  createEffect(() => { if (props.source.kind === "saved") setName(props.madeFor); });
  const unchanged = () => props.source.kind === "saved" && name().trim() === props.madeFor;
  return (
    <form class="save-profile" onSubmit={(event) => { event.preventDefault(); if (name().trim()) props.onSave(name().trim()); }}>
      <p class="picker-step"><small>4</small>Save as a profile</p>
      <div>
        <input type="text" aria-label="Profile name" placeholder="Name or initials" maxLength={40} value={name()} onInput={(event) => setName(event.currentTarget.value)} />
        <button type="submit" class="primary" disabled={!name().trim() || unchanged()}>{unchanged() ? "Saved" : "Save"}</button>
      </div>
      <small>Kept in this browser. Each pattern then adds its own ease.</small>
    </form>
  );
}
